/* eslint-disable no-console */
import React, { useState } from 'react';

import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Grid,
  Paper,
  Box,
  Divider,
  Tooltip,
  IconButton,
  ListItemButton,
  ListItemText,
  Collapse,
} from '@mui/material';
import { Alert, Link, Chip } from 'daaas-ui';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Icon from '@mdi/react';

import {
  mdiChevronDown,
  mdiChevronUp,
  mdiCubeSend,
  mdiOpenInNew,
  mdiEarth,
  mdiInformationOutline,
} from '@mdi/js';
import LockIcon from '@mui/icons-material/Lock';
import RequestKubeflowNamespace from '../components/UI/Dialogs/RequestKubeflowNamespace';
import BrandingDAS from '../components/Layout/BrandingDAS';
import caeLinks from '../utils/caeLinks';
import chartTimelineVariantShimmer from '../assets/images/chartTimelineVariantShimmer.png';
import VmSection from '../components/UI/VmSection';

import hubHeader from '../assets/images/hubHeader.svg';

import ContentContainer from '../components/Layout/ContentContainer';

const Root = styled('div')(({ theme }) => ({
  '.MuiChip-outlinedPrimary': {
    marginTop: theme.spacing(1),
    border: '3px solid',
    borderColor: `${theme.palette.primary.main} !important`,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderTopLeftRadius: theme.spacing(0.625),
    borderTopRightRadius: theme.spacing(0.625),
    borderBottomLeftRadius: theme.spacing(0.625),
    borderBottomRightRadius: theme.spacing(0.625),
    '& .MuiChip-label': {
      fontWeight: 'bolder',
      fontSize: '1rem',
    },
  },
  '.imgSizing': {
    width: '1.5rem',
    height: '1.5rem',
  },
  '.MuiTypography-root.MuiTypography-body1': {
    fontSize: 'inherit',
  },
  '.opensWindowMargin': {
    marginLeft: theme.spacing(1),
    verticalAlign: 'text-bottom',
  },
  '.hub-page__header': {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(0),
    marginTop: theme.spacing(4),
    paddingLeft: theme.spacing(2),
    backgroundImage: `url(${hubHeader})`,
    backgroundColor: '#173048',
    backgroundPosition: 'right top',
    backgroundSize: '40%',
    borderTopLeftRadius: theme.spacing(1.25),
    borderTopRightRadius: theme.spacing(1.25),
    backgroundRepeat: 'no-repeat',
    [theme.breakpoints.up('sm')]: {
      backgroundPosition: 'right top -10px',
      backgroundSize: '40%',
    },
    [theme.breakpoints.up('md')]: {
      backgroundPosition: 'right top -15px',
      backgroundSize: '30%',
    },
    [theme.breakpoints.up('lg')]: {
      backgroundPosition: 'right top -15px',
      backgroundSize: '25%',
    },
  },

  '.MuiPaper-root.MuiAccordion-root .MuiButtonBase-root.MuiAccordionSummary-root>.MuiAccordionSummary-expandIconWrapper':
    {
      margin: theme.spacing(2),
    },
  '& .hub_paper': {
    backgroundColor: '#F8F8F8',
    marginTop: theme.spacing(0),
    paddingTop: theme.spacing(0),
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    paddingBottom: theme.spacing(3),
    marginBottom: theme.spacing(6),
    textAlign: 'left',
    height: '100%',
    borderTopRightRadius: theme.spacing(0),
    borderTopLeftRadius: theme.spacing(0),
    boxSizing: 'border-box',
    '&:last-of-type': {
      marginBottom: theme.spacing(0),
    },
  },
  '.MuiPaper-root.MuiAccordion-root.Mui-expanded, .MuiPaper-root.MuiAccordion-root, .MuiPaper-root.MuiAccordion-root .MuiButtonBase-root.MuiAccordionSummary-root:hover ':
    {
      borderTopLeftRadius: theme.spacing(1.25),
      borderTopRightRadius: theme.spacing(1.25),
      borderBottomLeftRadius: theme.spacing(1.25),
      borderBottomRightRadius: theme.spacing(1.25),
      boxShadow: '0px 0px 6px #00000029',
    },

  '& .hub-page__list': {
    paddingLeft: theme.spacing(3),
    '&>li': {
      marginBottom: theme.spacing(0.5),
    },
  },
  '& .hub-page__list__no__bullets': {
    paddingLeft: theme.spacing(0),
    '&>li': {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: theme.spacing(1),
      listStyleType: 'none',
      '&>.MuiIconButton-root': {
        marginTop: theme.spacing(-1),
      },
    },
  },
  '& .hub-page__nestedList': {
    paddingLeft: theme.spacing(0),
    '&>li': {
      display: 'flex',
      alignItems: 'flex-start',
      listStyleType: 'none',
      '&>.MuiIconButton-root': {
        marginTop: theme.spacing(-1),
      },
      '&>ul': {
        paddingLeft: theme.spacing(6),
        listStyleType: 'disc',
      },
    },
  },
  '& .hub-page__gaeAccordion': {
    display: 'inline-flex',
    alignItems: 'flex-start',
    marginTop: theme.spacing(-2),
    marginLeft: theme.spacing(3),
    padding: theme.spacing(0.5, 1),
    '& .MuiListItemText-root': {
      flexGrow: 0,
    },
    '& svg': {
      marginLeft: theme.spacing(1),
    },
  },
  '& .hub-page__gaeCollapse': {
    '& ul': {
      marginTop: theme.spacing(1),
      paddingLeft: theme.spacing(6),
      '&>li': {
        '&>.MuiIconButton-root': {
          marginTop: theme.spacing(-1),
        },
      },
    },
  },
}));

export default function Hub() {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const statCanDomains = ['statcan.gc.ca', 'cloud.statcan.ca'];
  const isStatCanAuthenticated =
    isAuthenticated &&
    instance.getActiveAccount() &&
    statCanDomains.indexOf(
      instance.getActiveAccount().username.split('@')[1].toLowerCase()
    ) !== -1;
  const isExtAuthenticated =
    isAuthenticated &&
    instance.getActiveAccount() &&
    instance.getActiveAccount().username.split('@')[1].toLowerCase() ===
      'ext.statcan.ca';

  const { t, i18n } = useTranslation([
    'common',
    'aaw',
    'cae',
    'gae',
    'dialogs',
    'hub',
  ]);

  const [collapse, setCollapse] = React.useState(false);

  const handleCollapse = () => {
    setCollapse(!collapse);
  };

  // Kubeflow request namespace trigger functionality
  const [kubeflowDialogStatus, setkubeflowDialogStatus] = useState(false);

  const toggleKubeflowDialog = () => {
    setkubeflowDialogStatus(
      (prevKubeflowDialogStatus) => !prevKubeflowDialogStatus
    );
  };

  const handleKubeflowRequestNamespaceClick = () => {
    toggleKubeflowDialog();
  };

  React.useEffect(() => {
    setTimeout(() => {
      document.title = `${t('common:daaas')} - ${t('common:myHub')}`;
      // This is an authenticated page and won't be crawled, but settings the metadata anyways.
      document
        .querySelector("meta[name='description']")
        .setAttribute('content', t('hub:description'));
      document
        .querySelector("meta[name='keywords']")
        .setAttribute('content', t('hub:keywords'));
      document
        .querySelector("link[rel='canonical']")
        .setAttribute('href', t('hub:canonical'));
      document
        .querySelector("meta[name='dcterms.title']")
        .setAttribute('content', t('hub:dctermsTitle'));
      document
        .querySelector("meta[name='dcterms.description']")
        .setAttribute('content', t('hub:dctermsDescription'));
      document
        .querySelector("meta[name='dcterms.creator']")
        .setAttribute('content', t('common:dctermsCreator'));
      document
        .querySelector("meta[name='dcterms.language']")
        .setAttribute('content', t('common:dctermsLanguage'));
      document
        .querySelector("meta[name='dcterms.subject']")
        .setAttribute('content', t('common:dctermsSubject'));
    }, '100');
  }, [i18n.language]);

  // React.useEffect(() => {
  //   // TODO: add code here to ensure token is not expired, else get refresh token?
  //   // Once token is not expired then allow execution to continue?
  // }, []);

  return (
    <Root>
      <ContentContainer paddingBottom={false}>
        <Alert
          open
          severity="warning"
          closeable={false}
          sx={{
            mt: 4,
          }}
        >
          {t('hub:securityBanner')}
        </Alert>
        <Box className="hub-page__header">
          <Typography
            variant="h3"
            component="h1"
            sx={(theme) => ({
              color: theme.palette.common.white,
            })}
          >
            {t('common:myHub')}
          </Typography>
        </Box>
        <Paper className="hub_paper das-paper" variant="outlined">
          <Accordion defaultExpanded={isStatCanAuthenticated}>
            <AccordionSummary
              expandIcon={<Icon path={mdiChevronDown} size={1} />}
              aria-controls="das-tools-content"
              id="das-tools-header"
              sx={{ mt: 3 }}
            >
              <Typography
                component="h2"
                variant="h3"
                sx={{
                  ml: 2,
                }}
              >
                {t('common:dasToolsAndGuides')}
              </Typography>
            </AccordionSummary>
            <Divider sx={{ mx: 2 }} />
            <AccordionDetails>
              <Typography variant="body1" sx={{ px: 2, my: 2 }}>
                {t('common:dasTools')}
              </Typography>
              <Alert
                open
                severity="info"
                closeable={false}
                sx={{
                  mb: 3,
                  mx: 2,
                }}
              >
                {t('common:hubInfoText')}
              </Alert>
              <Grid
                container
                spacing={2}
                sx={{
                  pr: 2,
                  pl: 2,
                }}
              >
                <Grid
                  item
                  xs={12}
                  md={6}
                  lg
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box className="das-paper" sx={{ padding: 2 }}>
                    <Typography variant="h5" component="h3" sx={{ mb: 1 }}>
                      {t('common:aaw')}
                    </Typography>

                    <ul className="hub-page__list">
                      <li>
                        <Typography component="span">
                          <Link
                            href={`${t('aaw:aawUserGuideHref')}`}
                            target="_blank"
                          >
                            {t('aaw:aawUserGuide')}
                            <span className="screen-reader-text">
                              ({t('common:newTab')})
                            </span>
                          </Link>
                        </Typography>
                        <Icon
                          path={mdiOpenInNew}
                          size={0.8}
                          className="opensWindowMargin"
                        />
                      </li>
                    </ul>

                    <Chip
                      color="primary"
                      label={t('common:protectedB')}
                      icon={<LockIcon />}
                      variant="outlined"
                    />
                    <Tooltip title={t('aaw:aawToolTip')}>
                      <IconButton>
                        <Icon path={mdiInformationOutline} size={1} />
                      </IconButton>
                    </Tooltip>
                    <Divider sx={{ pb: 2, mb: 2 }} />
                    <ul className="hub-page__nestedList">
                      <li>
                        <Icon path={mdiCubeSend} size={1} />
                        <Typography component="span">
                          <Link
                            href={`${t('aaw:toolKubeflowLink')}`}
                            sx={{ ml: 1 }}
                            target="_blank"
                          >
                            Kubeflow
                          </Link>
                        </Typography>
                        <Tooltip title={t('aaw:toolKubeflowDescription')}>
                          <IconButton>
                            <Icon path={mdiInformationOutline} size={1} />
                          </IconButton>
                        </Tooltip>
                      </li>
                      <li>
                        <ul>
                          <li>
                            <Typography>
                              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                              <Link
                                component="button"
                                onClick={handleKubeflowRequestNamespaceClick}
                              >
                                {t('aaw:toolKubeflowMenu1Title')}
                              </Link>
                            </Typography>
                          </li>
                          <li>
                            <Typography component="span">
                              <Link
                                href={`${t('aaw:toolGrafanaLink')}`}
                                target="_blank"
                              >
                                Grafana
                              </Link>
                            </Typography>
                            <Tooltip title={t('aaw:toolGrafanaDescription')}>
                              <IconButton>
                                <Icon path={mdiInformationOutline} size={1} />
                              </IconButton>
                            </Tooltip>
                          </li>
                        </ul>
                      </li>
                    </ul>
                  </Box>
                  <Box className="das-paper" sx={{ mt: 2, padding: 2 }}>
                    <Typography variant="h5" component="h3" sx={{ mb: 1 }}>
                      {t('common:trainingResources')}
                    </Typography>
                    <ul className="hub-page__list">
                      <li>
                        <Typography component="span">
                          <Link
                            href={`${t('dialogs:helpTrainingButtonHref')}`}
                            target="_blank"
                          >
                            {t('dialogs:helpTrainingButton')}
                            <span className="screen-reader-text">
                              ({t('common:newTab')})
                            </span>
                          </Link>
                        </Typography>
                        <Icon
                          path={mdiOpenInNew}
                          size={0.8}
                          className="opensWindowMargin"
                        />
                      </li>
                    </ul>
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  lg
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <Box className="das-paper" sx={{ padding: 2 }}>
                    <Typography variant="h5" component="h3" sx={{ mb: 1 }}>
                      {t('common:cae')}
                    </Typography>

                    <ul className="hub-page__list">
                      <li>
                        <Typography component="span">
                          <Link
                            href={`${t('cae:caeUserGuideHref')}`}
                            target="_blank"
                          >
                            {t('cae:caeUserGuide')}
                            <span className="screen-reader-text">
                              ({t('common:newTab')})
                            </span>
                          </Link>
                        </Typography>
                        <Icon
                          path={mdiOpenInNew}
                          size={0.8}
                          className="opensWindowMargin"
                        />
                      </li>
                    </ul>
                    <Chip
                      color="primary"
                      label={t('common:protectedB')}
                      icon={<LockIcon />}
                      variant="outlined"
                    />
                    <Tooltip title={t('cae:caeToolTip')}>
                      <IconButton>
                        <Icon path={mdiInformationOutline} size={1} />
                      </IconButton>
                    </Tooltip>
                    <Divider sx={{ mb: 2, mt: 2 }} />
                    {isExtAuthenticated && (
                      <Alert
                        open
                        severity="info"
                        closeable={false}
                        sx={{
                          mb: 2,
                        }}
                      >
                        {t('cae:caeTools')}
                      </Alert>
                    )}
                    <ul className="hub-page__list__no__bullets">
                      {caeLinks()}
                    </ul>
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  lg
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <Box className="das-paper" sx={{ padding: 2 }}>
                    <Typography variant="h5" component="h3" sx={{ mb: 1 }}>
                      {t('common:gae')}
                    </Typography>

                    <ul className="hub-page__list">
                      <li>
                        <Typography component="span">
                          <Link
                            href={`${t('gae:gaeUserGuideHref')}`}
                            target="_blank"
                          >
                            {t('gae:gaeUserGuide')}
                            <span className="screen-reader-text">
                              ({t('common:newTab')})
                            </span>
                          </Link>
                        </Typography>
                        <Icon
                          path={mdiOpenInNew}
                          size={0.8}
                          className="opensWindowMargin"
                        />
                      </li>
                    </ul>
                    <Divider sx={{ my: 2 }} />
                    {isExtAuthenticated && (
                      <Alert
                        open
                        severity="info"
                        closeable={false}
                        sx={{
                          mb: 2,
                        }}
                      >
                        {t('gae:gaeTools')}
                      </Alert>
                    )}
                    <ul className="hub-page__list__no__bullets">
                      <li>
                        <Icon path={mdiEarth} size={1} />
                        <Typography
                          component="span"
                          variant="body1"
                          sx={{ ml: 1 }}
                        >
                          <Link
                            href={`${t('gae:arcGISEnterprisePlusLink')}`}
                            target="_blank"
                          >
                            {t('gae:arcGISEnterprisePlus')}
                          </Link>
                        </Typography>
                        <Tooltip
                          title={
                            <>
                              <Box sx={{ pb: 2 }}>
                                {t('gae:arcGISEnterpriseParagraph')}
                              </Box>
                              <Box>{t('gae:arcGISInsightsParagraph')}</Box>
                            </>
                          }
                        >
                          <IconButton
                            aria-label={`${t(
                              'gae:arcGISEnterpriseParagraph'
                            )}${t('gae:arcGISInsightsParagraph')}`}
                          >
                            <Icon path={mdiInformationOutline} size={1} />
                          </IconButton>
                        </Tooltip>
                      </li>
                      {isStatCanAuthenticated && (
                        <li>
                          <img
                            src={chartTimelineVariantShimmer}
                            alt=""
                            loading="lazy"
                            className="imgSizing"
                          />
                          <Typography
                            component="span"
                            variant="body1"
                            sx={{ ml: 1 }}
                          >
                            <Link
                              href={`${t('gae:virtualMachineGAESuiteLink')}`}
                              target="_blank"
                            >
                              {t('gae:virtualMachineGAESuite')}
                            </Link>
                          </Typography>
                          <Tooltip title={t('gae:virtualMachineGPUParagraph')}>
                            <IconButton>
                              <Icon path={mdiInformationOutline} size={1} />
                            </IconButton>
                          </Tooltip>
                        </li>
                      )}
                    </ul>
                    {isStatCanAuthenticated && (
                      <>
                        <ListItemButton
                          onClick={handleCollapse}
                          className="hub-page__gaeAccordion"
                          aria-expanded={collapse}
                          aria-controls="gae-links"
                        >
                          <ListItemText
                            sx={{ m: 0 }}
                            primary={
                              <Typography component="h4" variant="body1">
                                {t('gae:listOfTools')}
                              </Typography>
                            }
                          />
                          {collapse ? (
                            <Icon path={mdiChevronUp} size={1} />
                          ) : (
                            <Icon path={mdiChevronDown} size={1} />
                          )}
                        </ListItemButton>
                        <Collapse
                          id="gae-links"
                          className="hub-page__gaeCollapse"
                          in={collapse}
                          timeout="auto"
                        >
                          <ul>
                            <li>
                              <Typography variant="body1" component="span">
                                {t('gae:azurePostgreSQLPostGISHeading')}
                              </Typography>
                              <Tooltip title={t('gae:PostGIS')}>
                                <IconButton>
                                  <Icon path={mdiInformationOutline} size={1} />
                                </IconButton>
                              </Tooltip>
                            </li>
                            <li>
                              <Typography variant="body1" component="span">
                                {t('gae:azureBlobFileshareStorageHeading')}
                              </Typography>
                              <Tooltip title={t('gae:azureBlob')}>
                                <IconButton>
                                  <Icon path={mdiInformationOutline} size={1} />
                                </IconButton>
                              </Tooltip>
                            </li>
                            <li>
                              <Typography variant="body1" component="span">
                                {t('gae:qgisHeading')}
                              </Typography>
                              <Tooltip title={t('gae:QGIS')}>
                                <IconButton>
                                  <Icon path={mdiInformationOutline} size={1} />
                                </IconButton>
                              </Tooltip>
                            </li>
                            <li>
                              <Typography variant="body1" component="span">
                                {t('gae:arcGISProHeading')}
                              </Typography>
                              <Tooltip title={t('gae:ArcGIS')}>
                                <IconButton>
                                  <Icon path={mdiInformationOutline} size={1} />
                                </IconButton>
                              </Tooltip>
                            </li>
                            <li>
                              <Typography variant="body1" component="span">
                                {t('gae:notebooksAndIDEsHeading')}
                              </Typography>
                              <Tooltip title={t('gae:Notebooks')}>
                                <IconButton>
                                  <Icon path={mdiInformationOutline} size={1} />
                                </IconButton>
                              </Tooltip>
                            </li>
                          </ul>
                        </Collapse>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          <VmSection />
        </Paper>
      </ContentContainer>
      <BrandingDAS />
      <RequestKubeflowNamespace
        open={kubeflowDialogStatus}
        toggleDialog={toggleKubeflowDialog}
      />
    </Root>
  );
}
